import { lowerPalette, upperPalette, type EnvironmentPalette } from "./Palette";
import { deepMergeInto } from "./persist";

/**
 * Defaults do motor. Cada nível tem o seu recorte (`level.world`) e o seu
 * JSON em `src/levels/defaults/{id}.json`. Nunca gravar isto como save global.
 */
export const config = {
  bubble: {
    growRate: 42,
    minRadius: 6,
    maxRadius: 56,
    shrinkRate: 3.2,
    shrinkRateRel: 0.018,
    density: 0.0012,
    restitution: 0.72,
    airFriction: 0.008,
    waterFriction: 0.02,
    maxCount: 36,
    /** Fração do eixo menor: não se cria bolha mais perto da costura. */
    spawnKeepOut: 0.1,
  },
  merge: {
    /** Area feels more physical at the size cap. */
    sizeMetric: "area" as "area" | "radius",
    /** Max combined area as a fraction of π·maxRadius² (legado). */
    maxCombinedFactor: 1.0,
    /** Tecto após absorção: múltiplo de maxRadius. Sempre absorve até aqui. */
    absorbScale: 5,
  },
  divider: {
    /** `line` = costura horizontal; `oval` = costura a circundar o ecrã. */
    shape: "line" as "line" | "oval",
    /** Folga da oval às bordas (fração da largura/altura). */
    ovalInsetX: 0.07,
    ovalInsetY: 0.1,
    baseY: 0.5,
    /** Distância constante entre centros das esferas da linha (px). O número cresce com a largura. */
    pointSpacing: 4,
    absorbDistance: 4,
    attractionK: 0.00000012,
    attractionWhenSmall: 0.00000035,
    thickness: 16,
    sphereRadiusFactor: 0.62,
    wiggleAmp: 10,
    wiggleSpeed: 0.55,
    sizePulseAmp: 0.16,
    sizePulseSpeed: 0.42,
    forceToPixels: 28000,
    bumpStrength: 0.85,
    bumpSigma: 48,
    absorbSizeGrow: 0.04,
    absorbWiggleGrow: 0.07,
    absorbPulseGrow: 0.05,
    absorbSpeedGrow: 0.045,
    absorbRateMul: 5,
    /** Handles da Bézier ease-in-out (equiv. CSS cubic-bezier(in, 0, out, 1)). */
    absorbBezierIn: 0.42,
    absorbBezierOut: 0.58,
    /** Largura inicial da influência (fração da largura do ecrã). */
    absorbSpreadStart: 0.1,
    /** Bézier do fade esquerda/direita (1 no centro → 0 nos lados). */
    absorbFalloffIn: 0.25,
    absorbFalloffOut: 0.75,
    /** Bézier do equilíbrio: a influência local abre para a linha toda no fim. */
    absorbBalanceIn: 0.78,
    absorbBalanceOut: 0.92,
    thicknessMax: 280,
    /** Um lado (cima/baixo ou dentro/fora) a ocupar esta fração do ecrã = derrota. 0 = desligado. */
    spaceLoseAt: 0.7,
    wiggleAmpMax: 42,
    sizePulseAmpMax: 0.48,
    wiggleSpeedMax: 1.6,
    sizePulseSpeedMax: 1.35,
  },
  wind: {
    strength: 0.00018,
    turbulence: 1.15,
    noiseScale: 0.0016,
    noiseSpeed: 0.22,
    lateralBias: 1.35,
    verticalBias: 0.28,
  },
  ocean: {
    strength: 0.00024,
    turbulence: 0.9,
    noiseScale: 0.0011,
    noiseSpeed: 0.16,
    lateralBias: 1.0,
    fromBottom: 0.00012,
  },
  playerCurrent: {
    strokeRadius: 140,
    pushStrength: 0.0008,
    vacuumStrength: 0.00045,
    decay: 1.35,
    twoFingerThreshold: 2,
    /** Aceleração (px/s²) que corresponde a intensidade 1. */
    accelRef: 18000,
    /** Tecto do multiplicador de intensidade. */
    accelMax: 3.5,
    /** Toque: tempo parado no mesmo sítio para começar a bolha. */
    touchDwellMs: 500,
    /** Toque: folga (px) antes de o gesto passar a vento. */
    touchSlopPx: 20,
  },
  feel: {
    /** 1 = tiny bubbles almost ignore wind/current. */
    fieldFalloff: 1.15,
    minFieldInfluence: 0.06,
    attractionCurve: 2.2,
    minAttraction: 0.02,
    maxAttraction: 1,
  },
  field: {
    cols: 32,
    rows: 18,
    maxStored: 0.006,
  },
  physics: {
    gravityX: 0,
    gravityY: 0,
    wallThickness: 80,
    timeStepCapMs: 33,
  },
  score: {
    /** `pair` = cima+baixo juntos; `opposite` = antípodas na oval. */
    meetMode: "pair" as "pair" | "opposite",
    /** Erro máximo ao antípoda (graus). 30° = uma hora no relógio. */
    oppositeErrorDeg: 30,
    /** Janela em que um segundo lado ainda conta como “ao mesmo tempo”. */
    meetGraceMs: 2000,
    /** Folga extra no contacto entre as duas bolas (px). */
    meetSlack: 16,
    meetBase: 10,
    areaFactor: 0.02,
    /** Bézier da transição normal → brilho/cor do encontro. */
    meetBezierIn: 0.38,
    meetBezierOut: 0.62,
    /** Bézier do crescimento da aurora (0 → ecrã cheio aos `winAt`). */
    auroraBezierIn: 0.82,
    auroraBezierOut: 0.22,
    /** Pontuação em que o espectro enche o ecrã e o jogo acaba. */
    winAt: 5000,
  },
  visual: {
    swapEnvironments: false,
    contourWidth: 0.07,
    distortAmp: 1.0,
    metaballThreshold: 0.38,
    metaballPad: 1.85,
    colorPulse: 0.12,
    debugForces: true,
    upper: upperPalette as EnvironmentPalette,
    lower: lowerPalette as EnvironmentPalette,
  },
  particles: {
    enabled: true,
    /** Duração de vida (s). Opacidade máxima a meio (`peak`). */
    life: 10,
    peak: 5,
    /** Área (px²) por partícula: ecrãs maiores têm mais. */
    pxPerParticle: 9000,
    minCount: 28,
    maxCount: 520,
    size: 5.2,
    /** 0 = todas iguais; 10 = variação máxima em torno de `size`. */
    sizeRandom: 3,
    /** Conversão do vector de força em px/s. */
    advection: 120000,
    maxAlpha: 1,
  },
  trail: {
    enabled: true,
    /** Duração do fade (s). */
    life: 2.05,
    size: 5.5,
    sizeRandom: 2.8,
    /** Distância entre marcas (px). Menor = rasto mais denso. */
    spacing: 6,
    maxAlpha: 0.38,
    /** Velocidade da mudança de cor ao longo do gesto. */
    hueSpeed: 2.4,
    saturation: 0.58,
    /** Luminosidade em cima (fundo claro) e em baixo (fundo escuro). */
    lightAbove: 0.4,
    lightBelow: 0.7,
  },
  audio: {
    enabled: true,
    /** `synth-am` = Onirya; `synth-pass` = passagem / paralelo. */
    bedId: "synth-am" as "synth-am" | "synth-pass",
    master: 0.52,
    bed: 0.38,
    fx: 0.42,
    reverb: 0.55,
  },
};

export type GameConfig = typeof config;
export type BubbleConfig = GameConfig["bubble"];

/** Cópia fria do motor. Os níveis não escrevem aqui. */
const FACTORY: GameConfig = JSON.parse(JSON.stringify(config)) as GameConfig;

/** Repõe o config vivo nos defaults do motor, antes de aplicar um nível. */
export function resetConfig(target: GameConfig = config): void {
  deepMergeInto(target, JSON.parse(JSON.stringify(FACTORY)));
}
